import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

import type { CountryCode } from '@fluxora/types';

import { ThemeContext } from './context';
import type { Theme } from './types';

interface ThemeProviderProps {
  children: ReactNode;
  country?: CountryCode;
  defaultTheme?: Theme;
}

export function ThemeProvider({
  children,
  country,
  defaultTheme = 'light',
}: ThemeProviderProps): JSX.Element {
  const [theme, setTheme] = useState<Theme>(defaultTheme);

  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-theme', theme);
    if (country) root.setAttribute('data-country', country);
  }, [theme, country]);

  const toggleTheme = useCallback(() => {
    setTheme((t) => (t === 'light' ? 'dark' : 'light'));
  }, []);

  const value = useMemo(
    () => ({ theme, setTheme, toggleTheme, country: country ?? null }),
    [theme, toggleTheme, country],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
